/**
 * Abstract Style Management System - StyleMetadata Engine (Base Implementation)
 * @module abstractStyles/StyleMetadataEngine
 */

import type { StyleMetadata, GlobalDesignPanelProps } from '../../types';
import type { AggregatedStyleValues, PropertyMetadata } from './types';
import type { IStyleValuesProvider } from './IStyleValuesProvider';
import { StyleEngineBase } from './StyleEngineBase';
import { CSSPropertiesEngine } from './CSSPropertiesEngine';
import { CSSCustomPropertiesEngine } from './CSSCustomPropertiesEngine';

/**
 * Base engine for deriving style values from StyleMetadata.
 * Works without SDK - purely functional value transformation.
 *
 * This engine:
 * - Takes StyleMetadata (definitions + values) as input
 * - Automatically resolves CSS variable references (var(--name)) to actual values using theme service
 * - Decomposes all values to atomic properties with nesting levels
 * - Creates PropertyMetadata from definitions
 * - Returns AggregatedStyleValues
 * - Optionally scopes operations to specific properties (for group isolation)
 *
 * Use this when you have StyleMetadata but don't need SDK operations
 * (e.g., in lobby items, read-only previews).
 *
 * Can be extended by more powerful engines (like GlobalDesignStyleEngine)
 * to add SDK capabilities while reusing decomposition logic.
 */
export class StyleMetadataEngine
  extends StyleEngineBase
  implements IStyleValuesProvider
{
  constructor(
    protected readonly styleMetadata?: StyleMetadata,
    public readonly componentsAPI?: GlobalDesignPanelProps['componentsAPI'],
    allowedProperties?: string[],
  ) {
    super(allowedProperties);
  }

  /**
   * Asserts that styleMetadata is available and returns it.
   * @throws Error if styleMetadata was not provided
   */
  #requireStyleMetadata(): StyleMetadata {
    if (!this.styleMetadata) {
      throw new Error('StyleMetadataEngine: styleMetadata is required');
    }
    return this.styleMetadata;
  }

  /**
   * Returns built PropertyMetadata without running decomposition.
   * Use this when only metadata is needed (e.g. transferring to another engine).
   */
  getMetadata(): Record<string, PropertyMetadata> {
    const { styleDefinitions } = this.#requireStyleMetadata();
    const builtMetadata = this.buildMetadata(styleDefinitions);
    return this.filterRecord(builtMetadata);
  }

  /**
   * Derives all values from StyleMetadata or provided metadata.
   * Automatically resolves CSS variables using theme service if available.
   * Filters to only allowed properties if scoping is enabled.
   *
   * @param metadata - Optional property metadata (for subclasses that override this)
   * @returns Aggregated style values with mode discrimination
   */
  getAllValues(
    metadata?: Record<string, PropertyMetadata>,
  ): AggregatedStyleValues {
    const { styleDefinitions, styles } = this.#requireStyleMetadata();
    const builtMetadata = metadata || this.buildMetadata(styleDefinitions);

    // Filter to allowed properties (group scope)
    const filteredCssProps = this.filterRecord(styles.cssProperties);
    const filteredCustomProps = this.filterRecord(styles.cssCustomProperties);
    const filteredCssPropsDefs = this.filterRecord(
      styleDefinitions.cssProperties,
    );
    const filteredCustomPropsDefs = this.filterRecord(
      styleDefinitions.cssCustomProperties,
    );
    const filteredMetadata = this.filterRecord(builtMetadata);

    return this.deriveAggregatedValues(
      filteredCssProps,
      filteredCustomProps,
      filteredCssPropsDefs,
      filteredCustomPropsDefs,
      filteredMetadata,
    );
  }

  /**
   * Core logic for deriving aggregated style values.
   * Delegates to base class method with componentsAPI.
   *
   * @param cssPropsRaw - Raw CSS properties values
   * @param customPropsRaw - Raw CSS custom properties values
   * @param cssPropsDefinitions - CSS properties definitions
   * @param customPropsDefinitions - CSS custom properties definitions
   * @param metadata - Property metadata
   * @returns Aggregated style values with mode discrimination
   */
  protected deriveAggregatedValues(
    cssPropsRaw: Record<string, string>,
    customPropsRaw: Record<string, string>,
    cssPropsDefinitions: Record<string, any>,
    customPropsDefinitions: Record<string, any>,
    metadata: Record<string, PropertyMetadata>,
  ): AggregatedStyleValues {
    return super.deriveAggregatedValues(
      cssPropsRaw,
      customPropsRaw,
      cssPropsDefinitions,
      customPropsDefinitions,
      metadata,
      this.componentsAPI,
    );
  }

  /**
   * Builds PropertyMetadata from style definitions using shared builders.
   */
  protected buildMetadata(
    styleDefinitions: StyleMetadata['styleDefinitions'],
  ): Record<string, PropertyMetadata> {
    const cssMetadata = CSSPropertiesEngine.buildMetadata(
      styleDefinitions.cssProperties || {},
      this.styleMetadata?.styles.cssProperties || {},
    );
    const customMetadata = CSSCustomPropertiesEngine.buildMetadata(
      styleDefinitions.cssCustomProperties || {},
    );

    return { ...cssMetadata, ...customMetadata };
  }
}
